import {
  getSubscriptionForOtherProducts,
  mapDefaultSubscribedCountry,
  mapSubscribedCountries,
  retainTimePeriod,
  saveErrorLogForOnChange,
} from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';

import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  actionCreatorsHitlistsRetailerClient,
  actionCreatorsInitialFiltersProducts,
  actionCreatorsProducts,
} from '@state/Index';
import { HitlistsRetailerClientState } from '@state/reducers/hitlist-retailer-client/HitlistsRetailerClientReducer';
import { InitialFiltersProductsState } from '@state/reducers/initial-filter-products/InitialFiltersProductsReducer';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { Option, PageName } from '@utils/pageConfig/Types';
import { ClientSubscription } from '@models/Opa';
import { ProductType } from '@pages/product-analyzer/Product';
import { MainFilterStateProducts } from '@state/reducers/main-filter-products/MainFiltersProductsReducer';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';

/*
  Hooks to restore the multibanner retailer client for product page
 */
export interface RestoreProductMultibannerRetailerViewProps {
  pagesConfig: PagesConfig;
  hitlistRetailerClientState: HitlistsRetailerClientState;
  hitllistInitialFilterState: InitialFiltersProductsState;
  productMainFilterState?: MainFilterStateProducts;
  appliedCountry: Option[];
  pageName: PageName;
}

export const useRestoreProductMultibannerRetailerClient = (): {
  restoreProductMultibannerRetailerClient: (props: RestoreProductMultibannerRetailerViewProps) => void;
} => {
  const dispatch = useDispatch();
  const { updateInitialFiltersProducts } = bindActionCreators(actionCreatorsInitialFiltersProducts, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);

  const restoreProductMultibannerRetailerClient = (props: RestoreProductMultibannerRetailerViewProps) => {
    const {
      pagesConfig,
      hitlistRetailerClientState,
      hitllistInitialFilterState,
      productMainFilterState,
      appliedCountry,
      pageName,
    } = props;

    if (hitlistRetailerClientState && hitllistInitialFilterState) {
      //Get current subscription for product analyzer page
      const hitlistSubscriptions: ClientSubscription[] = getSubscriptionForOtherProducts(
        ProductType.ProductAnalyzer,
        pagesConfig,
        hitlistRetailerClientState.clientId,
      );

      const hitlistsSubscribedCountries = mapSubscribedCountries(hitlistSubscriptions);

      if (hitlistsSubscribedCountries.length === 0) {
        return;
      }

      const defaultSelectedHitlistCountry = mapDefaultSubscribedCountry(hitlistSubscriptions, appliedCountry);

      const defaultCountryCode = defaultSelectedHitlistCountry.countryCode;

      //Get the initial filter data based on country
      const filtersDataBasedOnCountry = hitllistInitialFilterState.initialFilters.find(
        (filter) => filter.country === defaultCountryCode,
      );

      if (!filtersDataBasedOnCountry) {
        saveErrorLogForOnChange(pagesConfig.clientCountries, pagesConfig.appliedFilters, pageName);
        return;
      }

      // Update product main filter state if this hooks is being called from RI page
      if (productMainFilterState) {
        // Get latest period for Product Analyzer
        const currTimeFrame =
          productMainFilterState.timeFrame === Periodicity.Monthly ? Periodicity.Monthly : Periodicity.Weekly;

        const currPeriod = retainTimePeriod(filtersDataBasedOnCountry, currTimeFrame);

        const productMainFilterSettings = {
          timeFrame: productMainFilterState.timeFrame,
          period: currPeriod,
          comparisonPeriod: productMainFilterState.comparisonPeriod,
          selectedGrowthType: productMainFilterState.selectedGrowthType,
          country: productMainFilterState.country,
        };

        updateMainFiltersProducts(productMainFilterSettings);
      }

      //Update hitlist retailer client and initial filter Redux state
      updateHitlistsRetailerClient(hitlistRetailerClientState);
      updateInitialFiltersProducts(hitllistInitialFilterState);
    }
  };

  return { restoreProductMultibannerRetailerClient };
};
